import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCan } from '@/hooks/useCan'
import { MobilisationStepper } from './MobilisationStepper'
import { Step1ActivateUsers } from './Step1ActivateUsers'
import { MobilisationRequestPanel } from '@/features/mobilisation/components/MobilisationRequestPanel'
import { Step2Communication } from './Step2Communication'
import { Step3MobilisationTasks } from './Step3MobilisationTasks'
import { Step4TechnicalChecklist } from './Step4TechnicalChecklist'
import { Step5ImpactAssessmentForSet } from './Step5ImpactAssessment'
import {
    useMobilisationStatus,
    useMobilisationTeams,
    useMobiliseUsers,
    useSendTeamNotification,
    useSendAllNotifications,
    useMobilisationStatusBySet,
    useMobilisationTeamsBySet,
    useMobiliseUsersBySet,
    useSendTeamNotificationBySet,
    useSendAllNotificationsBySet,
    useDeactivateUsersBySet,
    useActivateTeamUsersBySet,
    useDeactivateTeamUsersBySet,
    useRecoverySetDashboard,
    MOBILISATION_STEP_LABELS,
    type NotifyAllProgressEvent,
} from '../../hooks'

const TOTAL_STEPS = 5

interface MobilisationTabProps {
    /** When in Recovery Set detail (Recovery Manager) */
    setId?: number
    /** When in Runbook (single request) */
    requestID?: number
    /**
     * How many steps to show. Recovery Manager passes 3: there the Technical Checklist and the
     * Impact Assessment are their own views (Assess), not steps four and five (12 Oct 2026).
     */
    totalSteps?: number
}

export function MobilisationTab({ setId, requestID, totalSteps = TOTAL_STEPS }: MobilisationTabProps) {
    const { t } = useTranslation()
    const [currentStep, setCurrentStep] = useState(1)
    // Mobilising (activating users, notifying teams, the checklists) is managing the recovery:
    // recover-requests.update. Without it the steps are shown with every control disabled.
    const canManage = useCan('recover-requests.update')
    const bySet = setId != null && setId > 0
    const statusByRequest = useMobilisationStatus(bySet ? null : requestID ?? null)
    const statusBySet = useMobilisationStatusBySet(bySet ? setId : null)
    const statusData = bySet ? statusBySet.data : statusByRequest.data
    const teamsByRequest = useMobilisationTeams(bySet ? null : requestID ?? null)
    const teamsBySet = useMobilisationTeamsBySet(bySet ? setId : null)
    const teams = (bySet ? teamsBySet.data : teamsByRequest.data) ?? []
    const teamsLoading = bySet ? teamsBySet.isLoading : teamsByRequest.isLoading
    const mobiliseUsersRequest = useMobiliseUsers(bySet ? null : requestID ?? null)
    const mobiliseUsersSet = useMobiliseUsersBySet(bySet ? setId : null)
    const deactivateUsersSet = useDeactivateUsersBySet(bySet ? setId : null)
    const activateTeamUsersSet = useActivateTeamUsersBySet(bySet ? setId : null)
    const deactivateTeamUsersSet = useDeactivateTeamUsersBySet(bySet ? setId : null)
    const mobiliseUsers = bySet ? mobiliseUsersSet : mobiliseUsersRequest
    type SendResultByTeam = Record<number, { sms?: { sent: number; failed?: number; errorMessage?: string }; email?: { sent: number; failed?: number; errorMessage?: string } }>
    const [lastSendResult, setLastSendResult] = useState<SendResultByTeam>({})
    const clearResultTimerRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
    type SendProgressByTeam = Record<number, { sms?: { sent: number; total: number }; email?: { sent: number; total: number } }>
    const [sendProgress, setSendProgress] = useState<SendProgressByTeam>({})

    const handleSendComplete = useCallback((teamId: number, channel: 'sms' | 'email', result: { sent: number; failed?: number; errorMessage?: string }) => {
        setLastSendResult((prev) => ({
            ...prev,
            [teamId]: { ...prev[teamId], [channel]: result },
        }))
        // Keep progress bar visible at final state until user leaves the screen (do not clear sendProgress here)
        const existing = clearResultTimerRef.current[teamId]
        if (existing) clearTimeout(existing)
        clearResultTimerRef.current[teamId] = setTimeout(() => {
            setLastSendResult((p) => {
                const next = { ...p }
                delete next[teamId]
                return next
            })
            delete clearResultTimerRef.current[teamId]
        }, 5000)
    }, [])

    const handleSendProgress = useCallback((teamId: number, channel: 'sms' | 'email', sent: number, total: number) => {
        setSendProgress((prev) => ({
            ...prev,
            [teamId]: { ...prev[teamId], [channel]: { sent, total } },
        }))
    }, [])

    const handleSendError = useCallback((teamId: number, channel: 'sms' | 'email') => {
        setSendProgress((prev) => {
            const next = { ...prev }
            if (!next[teamId]) return next
            const c = { ...next[teamId] }
            delete c[channel]
            next[teamId] = Object.keys(c).length > 0 ? c : undefined
            if (next[teamId] === undefined) delete next[teamId]
            return next
        })
    }, [])

    useEffect(() => {
        return () => {
            Object.values(clearResultTimerRef.current).forEach(clearTimeout)
            clearResultTimerRef.current = {}
        }
    }, [])

    // Clear progress bars when user leaves the Communications step (step 2)
    useEffect(() => {
        if (currentStep !== 2) {
            setSendProgress({})
        }
    }, [currentStep])

    const [sendAllProgress, setSendAllProgress] = useState<NotifyAllProgressEvent | null>(null)

    const sendTeamNotificationRequest = useSendTeamNotification(bySet ? null : requestID ?? null)
    const sendTeamNotificationSet = useSendTeamNotificationBySet(bySet ? setId : null, bySet ? {
        onSendComplete: handleSendComplete,
        onProgress: handleSendProgress,
        onSendError: handleSendError,
    } : undefined)
    const sendTeamNotification = bySet ? sendTeamNotificationSet : sendTeamNotificationRequest
    const sendAllNotificationsRequest = useSendAllNotifications(bySet ? null : requestID ?? null)
    const sendAllNotificationsSet = useSendAllNotificationsBySet(bySet ? setId : null, {
        onProgress: bySet ? (ev) => setSendAllProgress(ev) : undefined,
    })
    const sendAllNotifications = bySet ? sendAllNotificationsSet : sendAllNotificationsRequest

    useEffect(() => {
        if (!sendAllNotifications.isPending && sendAllProgress != null) {
            const t = setTimeout(() => setSendAllProgress(null), 8000)
            return () => clearTimeout(t)
        }
    }, [sendAllNotifications.isPending, sendAllProgress])

    const { data: dashboard } = useRecoverySetDashboard()
    const dashboardSummary = bySet && setId != null ? dashboard?.summary ?? null : null
    const dashboardStats = bySet && setId != null ? dashboard?.statisticsBySetId?.[setId] ?? null : null

    const completedCount = statusData?.completedSteps ?? 0
    const stepLabels = MOBILISATION_STEP_LABELS.map((label) =>
        t(`recoverRequests.mobilisation.stepLabel.${label.replace(/\s+/g, '')}`, label)
    )

    const goNext = useCallback(() => setCurrentStep((s) => Math.min(totalSteps, s + 1)), [totalSteps])
    const goPrev = useCallback(() => setCurrentStep((s) => Math.max(1, s - 1)), [])
    const nextLabel = currentStep < totalSteps ? stepLabels[currentStep] : null

    const handleActivateUsers = useCallback(() => {
        mobiliseUsers.mutate(undefined as any)
    }, [mobiliseUsers])

    const handleDeactivateUsers = useCallback(() => {
        if (!bySet) return
        deactivateUsersSet.mutate(undefined as any)
    }, [bySet, deactivateUsersSet])

    const handleToggleTeam = useCallback(
        (teamId: number, isActivated: boolean) => {
            if (!bySet) return
            if (isActivated) {
                deactivateTeamUsersSet.mutate({ teamId })
            } else {
                activateTeamUsersSet.mutate({ teamId })
            }
        },
        [bySet, activateTeamUsersSet, deactivateTeamUsersSet]
    )

    const handleSendSms = useCallback(
        (teamId: number) => sendTeamNotification.mutate({ teamId, channel: 'sms' }),
        [sendTeamNotification]
    )
    const handleSendEmail = useCallback(
        (teamId: number) => sendTeamNotification.mutate({ teamId, channel: 'email' }),
        [sendTeamNotification]
    )
    const handleSendToAll = useCallback(() => sendAllNotifications.mutate(), [sendAllNotifications])

    const sending =
        sendTeamNotification.isPending && sendTeamNotification.variables
            ? { teamId: sendTeamNotification.variables.teamId, channel: sendTeamNotification.variables.channel }
            : null

    return (
        <div className="space-y-6">
            <MobilisationStepper
                currentStep={currentStep}
                completedCount={completedCount}
                stepLabels={stepLabels}
                onStepClick={setCurrentStep}
                dashboardSummary={dashboardSummary}
                dashboardStats={dashboardStats}
                totalSteps={totalSteps}
            />

            <div
                className={
                    currentStep === 5
                        ? 'rounded-lg border bg-card p-6 w-[calc(100vw-8rem)] max-w-[1600px] relative left-1/2 -translate-x-1/2 mr-6'
                        : 'rounded-lg border bg-card p-6'
                }
            >
                {/* A disabled fieldset disables every button, input and checkbox inside it at once,
                    so a step added later is read-only here without having to know about it. */}
                {currentStep === 1 && bySet && <MobilisationRequestPanel setId={setId!} />}
                <fieldset disabled={!canManage} className="m-0 min-w-0 border-0 p-0">
                    {/* For a recovery set, standby users are let in by a mobilisation request: asked
                        for by one administrator, approved by another, each person confirmed by
                        their team lead (features/mobilisation). It has its own permissions, so it
                        sits outside this tab's fieldset (below). */}
                    {currentStep === 1 && !bySet && (
                        <Step1ActivateUsers
                            teams={teams}
                            isLoading={teamsLoading}
                            isActivating={mobiliseUsers.isPending || (bySet && deactivateUsersSet.isPending) || false}
                            onActivateUsers={handleActivateUsers}
                            onDeactivateUsers={bySet ? handleDeactivateUsers : undefined}
                            onToggleTeam={bySet ? handleToggleTeam : undefined}
                        />
                    )}
                    {currentStep === 2 && (
                        <Step2Communication
                            teams={teams}
                            onSendSms={handleSendSms}
                            onSendEmail={handleSendEmail}
                            onSendToAll={handleSendToAll}
                            sending={sending ?? undefined}
                            sendingAll={sendAllNotifications.isPending}
                            sendAllProgress={bySet ? sendAllProgress : undefined}
                            sendProgress={bySet ? sendProgress : undefined}
                            lastSendResult={bySet ? lastSendResult : undefined}
                        />
                    )}
                    {currentStep === 3 && <Step3MobilisationTasks teams={teams} setId={bySet ? setId : undefined} />}
                    {currentStep === 4 && (
                        <Step4TechnicalChecklist
                            teams={teams}
                            setId={bySet ? setId : undefined}
                            onOpenImpactAssessment={() => setCurrentStep(5)}
                        />
                    )}
                    {currentStep === 5 && <Step5ImpactAssessmentForSet setId={bySet ? setId : undefined} />}
                </fieldset>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
                <Button
                    variant="outline"
                    onClick={goPrev}
                    disabled={currentStep <= 1}
                >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    {t('common.back')}
                </Button>
                <div className="flex items-center gap-2">
                    {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                        <button
                            key={step}
                            type="button"
                            onClick={() => setCurrentStep(step)}
                            className={`h-2 w-2 rounded-full transition-colors ${
                                step === currentStep
                                    ? 'bg-primary'
                                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                            }`}
                            aria-label={t('recoverRequests.mobilisation.goToStep', { step }, `Go to step ${step}`)}
                        />
                    ))}
                </div>
                <Button
                    onClick={goNext}
                    disabled={currentStep >= totalSteps}
                >
                    {nextLabel
                        ? `${t('recoverRequests.mobilisation.next', 'Next')}: ${nextLabel}`
                        : t('recoverRequests.mobilisation.next', 'Next')}
                    <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
            </div>
        </div>
    )
}
